import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useSite } from "@/contexts/SiteContext";
import { formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const { settings } = useSite();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) nav("/admin/dashboard"); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in.");
      nav("/admin/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-black to-emerald_brand" />
        <div className="absolute inset-0 grain opacity-40" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <Link to="/" className="flex items-center gap-3 text-white/80 hover:text-gold text-xs tracking-widest uppercase">
            <ArrowLeft size={14} /> Back to Website
          </Link>
          <div>
            <img src={settings.logo} alt="Hermanos" className="h-16 w-auto mb-8" />
            <p className="section-label text-gold mb-4">Enterprise CMS</p>
            <h1 className="serif-display text-4xl md:text-5xl leading-tight">
              Manage the <span className="gold-text">Hermanos</span> business floor.
            </h1>
            <p className="mt-5 text-white/70 max-w-md">
              Update catalogues, brands, media, and business enquiries. Authorized personnel only.
            </p>
          </div>
          <p className="text-[0.65rem] tracking-widest uppercase text-white/50">© Hermanos Industries Pvt Ltd</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <div className="flex items-center gap-2 text-gold mb-6 lg:hidden">
            <ShieldCheck size={18} />
            <p className="section-label">Admin Portal</p>
          </div>
          <h2 className="serif-display text-4xl md:text-5xl text-foreground">Sign In</h2>
          <p className="mt-2 text-sm text-foreground/70">Enter your admin credentials to access the CMS.</p>

          <form data-testid={ADMIN.loginForm} onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label className="text-xs tracking-wider uppercase text-foreground/70">Email</Label>
              <Input
                data-testid={ADMIN.loginEmail}
                type="email"
                maxLength={40}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-11 rounded-sm bg-card"
                placeholder="admin@hermanosindustries.com"
              />
            </div>
            <div>
              <Label className="text-xs tracking-wider uppercase text-foreground/70">Password</Label>
              <div className="mt-2 relative">
                <Input
                  data-testid={ADMIN.loginPassField}
                  type={show ? "text" : "password"}
                  maxLength={25}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 rounded-sm bg-card pr-11"
                  placeholder="••••••••"
                />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/50 hover:text-foreground">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button
              data-testid={ADMIN.loginSubmit}
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-sm bg-gold hover:bg-gold-400 text-navy-900 font-semibold tracking-widest uppercase text-xs"
            >
              {loading ? <><Loader2 size={16} className="animate-spin mr-2" /> Signing In…</> : "Sign In"}
            </Button>
            <div className="text-center text-[0.7rem] text-foreground/50 mt-4">
              Authorized personnel only. Unauthorized access is prohibited.
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
